// FILE: src/components/shared/ScreenHeader.js
//
// ScreenHeader — the standard header for learner-facing screens, matching
// the Study Plan Command Center conventions (docs/ux-system.md):
//
//   ‹ Back to dashboard                              [actions…]
//   EYEBROW LABEL
//   Screen Title  (Press Start 2P)
//   optional subtitle
//
// Presentational only — no data fetching, no Redux, no hooks.
//
//   <ScreenHeader
//     onBack={() => onNavigate('dashboard')}
//     eyebrow="Practice Exam · PMP"
//     title="Session Results"
//     actions={<><button …/></>}
//   />

import React from 'react';
import CoachAvatar from '../coach/CoachAvatar';
import scss from './ScreenHeader.module.css';

/**
 * @param {function} [onBack]      Click handler for the back link. Omit to hide it.
 * @param {string}   [backLabel]   Back link text (default "Back to dashboard").
 * @param {string}   [backTestId]  data-testid for the back link.
 * @param {string}   [eyebrow]     Small uppercase context label above the title.
 * @param {string}   [title]       Screen title, rendered in the headline font.
 *                                Omit for back-link-only headers (centered coach screens).
 * @param {React.ReactNode} [subtitle] Optional line under the title.
 * @param {React.ReactNode} [actions]  Right-aligned action buttons.
 * @param {React.ReactNode} [promo]    Banner variant only: promotional card
 *                                rendered between the title block and the
 *                                actions column (e.g. the Discord promo).
 * @param {string}   [variant]     'plain' (default) or 'banner' — the navy
 *                                Study Plan hero treatment (the "topmast").
 * @param {string}   [coachPose]   Banner only: CoachAvatar pose. Omit for no
 *                                avatar (e.g. when the screen body already
 *                                has its own coach moment — one per screen).
 * @param {number}   [coachHeightPx] Banner avatar height (default 170).
 * @param {boolean}  [coachCentered] Banner only: vertically center the avatar
 *                                instead of the default bottom-flush bleed —
 *                                for tall banners with content tiles inside.
 * @param {string}   [titleId]     id for the h1 (aria-labelledby targets).
 * @param {string}   [testId]      data-testid for the header wrapper.
 * @param {string}   [className]   Extra class on the wrapper.
 * @param {React.ReactNode} [children] Banner variant only: content rendered
 *                                INSIDE the banner on the same row as the
 *                                avatar and title (e.g. the Session Setup
 *                                mode tiles). Assumes no `actions`.
 */
export default function ScreenHeader({
  onBack,
  children,
  backLabel = 'Back to dashboard',
  backTestId,
  eyebrow,
  title,
  subtitle,
  actions,
  promo,
  titleId,
  variant = 'plain',
  coachPose,
  coachHeightPx = 170,
  coachCentered = false,
  testId,
  className = '',
}) {
  const isBanner = variant === 'banner';
  return (
    <header className={[scss.header, className].filter(Boolean).join(' ')} data-testid={testId}>
      {onBack && (
        <button
          type="button"
          className={scss.backLink}
          onClick={onBack}
          data-testid={backTestId}
        >&lsaquo; {backLabel}</button>
      )}
      {(title || eyebrow || subtitle || actions || promo || children) && (
        <div className={isBanner ? [scss.banner, children ? scss.bannerInline : null, promo ? scss.bannerPromo : null].filter(Boolean).join(' ') : scss.row}>
          {isBanner && coachPose && (
            <div className={coachCentered ? `${scss.bannerAvatar} ${scss.bannerAvatarCentered}` : scss.bannerAvatar}>
              <CoachAvatar pose={coachPose} heightPx={coachHeightPx} decorative />
            </div>
          )}
          <div className={scss.titleBlock}>
            {eyebrow && <p className={scss.eyebrow}>{eyebrow}</p>}
            {title && <h1 id={titleId} className={scss.title}>{title}</h1>}
            {subtitle && <div className={scss.subtitle}>{subtitle}</div>}
          </div>
          {promo && isBanner && <div className={scss.promoSlot}>{promo}</div>}
          {actions && <div className={isBanner ? scss.bannerActions : scss.actions}>{actions}</div>}
          {children && isBanner && <div className={scss.bannerBody}>{children}</div>}
        </div>
      )}
    </header>
  );
}
